<template>
  <div class="ocr-upload-file-grid">
    <div class="ocr-file-panel-header">
      <div class="ocr-file-panel-heading">
        <h3 class="ocr-file-panel-title">{{ title }}</h3>
        <p class="ocr-file-panel-subtitle">Select or process individual files</p>
      </div>
      <v-btn
        icon
        variant="text"
        size="small"
        :loading="loadingList"
        aria-label="Refresh"
        @click="loadFiles"
      >
        <v-icon size="20">mdi-refresh</v-icon>
      </v-btn>
    </div>

    <div v-if="loadingList" class="ocr-excel-grid-loading">
      <div v-for="n in 4" :key="n" class="ocr-excel-grid-card ocr-excel-grid-card--skeleton" />
    </div>
    <p v-else-if="!files.length" class="ocr-empty-cell">No files in upload folder.</p>
    <div v-else class="ocr-excel-grid">
      <article
        v-for="file in files"
        :key="file"
        class="ocr-excel-grid-card"
        :class="{
          'ocr-excel-grid-card--selected': isSelected(file),
          'ocr-excel-grid-card--processing': processingFile === file,
        }"
      >
        <div class="ocr-excel-grid-thumb ocr-upload-grid-thumb" @click="toggle(file)">
          <div v-if="thumbLoading[file]" class="ocr-excel-grid-thumb-loading">Loading…</div>
          <img
            v-else-if="thumbUrls[file]"
            :src="thumbUrls[file]"
            class="ocr-upload-grid-thumb-img"
            alt=""
          />
          <div v-else class="ocr-excel-grid-thumb-empty">
            <v-icon :icon="iconFor(file)" size="32" />
          </div>
        </div>
        <p class="ocr-excel-grid-caption" :title="file">{{ file }}</p>
        <p v-if="meta[file]?.page_count" class="ocr-excel-grid-meta">{{ meta[file].page_count }} page(s)</p>
        <div class="ocr-excel-grid-actions">
          <v-checkbox
            :model-value="isSelected(file)"
            density="compact"
            hide-details
            label="Select"
            @update:model-value="setSelected(file, $event)"
          />
          <v-btn size="x-small" variant="tonal" :loading="processingFile === file" @click="$emit('process-one', file)">
            Process
          </v-btn>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";

export default {
  name: "OcrUploadFileGrid",
  props: {
    title: { type: String, default: "Uploaded files" },
    bucket: { type: String, default: "uploaded" },
    refreshKey: { type: Number, default: 0 },
    selected: { type: Array, default: () => [] },
    processingFile: { type: String, default: null },
    extensions: {
      type: String,
      default: ".pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tif,.tiff",
    },
  },
  emits: ["update:selected", "process-one", "error"],
  data() {
    return {
      loadingList: false,
      files: [],
      thumbUrls: {},
      thumbLoading: {},
      meta: {},
      objectUrls: [],
    };
  },
  watch: {
    refreshKey: { handler() { this.loadFiles(); } },
  },
  beforeUnmount() {
    this.revokeUrls();
  },
  mounted() {
    this.loadFiles();
  },
  methods: {
    iconFor(name) {
      const lower = name.toLowerCase();
      if (lower.endsWith(".pdf")) return "mdi-file-pdf-box";
      if (/\.(png|jpe?g|gif|webp)$/.test(lower)) return "mdi-file-image";
      return "mdi-file-document-outline";
    },
    revokeUrls() {
      this.objectUrls.forEach((u) => URL.revokeObjectURL(u));
      this.objectUrls = [];
      this.thumbUrls = {};
    },
    isSelected(name) {
      return this.selected.includes(name);
    },
    toggle(name) {
      this.setSelected(name, !this.isSelected(name));
    },
    setSelected(name, on) {
      const next = new Set(this.selected);
      if (on) next.add(name);
      else next.delete(name);
      this.$emit("update:selected", [...next]);
    },
    async loadFiles() {
      const quiet = this.files.length > 0;
      if (!quiet) this.loadingList = true;
      try {
        const params = { bucket: this.bucket };
        if (this.extensions) params.extensions = this.extensions;
        const { data } = await ocrApi.get("api/v1/files", { params });
        const next = data.files || [];
        const same = next.length === this.files.length && next.every((name, i) => name === this.files[i]);
        this.files = next;
        if (!same) {
          this.revokeUrls();
          this.meta = {};
          await this.loadThumbnails(this.files);
        }
      } catch (e) {
        this.$emit("error", formatOcrApiError(e, "Could not list files."));
      } finally {
        this.loadingList = false;
      }
    },
    async loadThumbnails(names) {
      const batch = names.slice(0, 24);
      await Promise.all(
        batch.map(async (name) => {
          this.thumbLoading = { ...this.thumbLoading, [name]: true };
          try {
            const { data: metaData } = await ocrApi.get(
              `api/v1/files/${encodeURIComponent(name)}/preview-meta`,
              { params: { bucket: this.bucket } },
            );
            this.meta = { ...this.meta, [name]: metaData };
            if (metaData.kind === "pdf" || metaData.kind === "image") {
              const res = await ocrApi.get(
                `api/v1/files/${encodeURIComponent(name)}/thumbnail`,
                { params: { bucket: this.bucket }, responseType: "blob" },
              );
              const url = URL.createObjectURL(res.data);
              this.objectUrls.push(url);
              this.thumbUrls = { ...this.thumbUrls, [name]: url };
            }
          } catch {
            /* icon fallback */
          } finally {
            this.thumbLoading = { ...this.thumbLoading, [name]: false };
          }
        }),
      );
    },
  },
};
</script>
